/*
  dd-bundle-guide.js
  ─────────────────────────────────────────────────────────────────
  Web component for the PDP "How much hair is needed?" section.

  Behavior:
    - Click a bundle card with data-qty-value > 0 → set the product
      form's quantity input to that value (dispatch input + change so
      Dawn's <quantity-input> +/- buttons and any cart listeners
      update) and smooth-scroll to the Add to Cart button.
    - Cards with data-qty-value=0 are inert (the "0 bundles / client's
      natural hair" reference column).
    - When data-interactive="false" (merchant disabled the toggle),
      no click handlers are wired at all.
      - On mobile/tablet the cards live in a clipped 1.5-card peek
      track. Touch uses native overflow-x; this component adds
      mouse-drag. Dawn <slider-component> still wraps the track
      (counter/arrows stay in markup, CSS-hidden; peek is the cue).
      A drag that moves past 8px suppresses the following click
      so qty-set doesn't fire mid-swipe.

  Failure modes covered:
    - No qty input on the page (single-variant template, or qty
      hidden) → fall through to scroll-only.
    - Main ATC offscreen on mobile (sticky footer is the visible
      buy surface) → scroll target falls back to <dd-sticky-footer>.

  Singleton-safe: defining the same custom element twice throws in
  modern browsers, so guard with `customElements.get`.
*/
(function () {
  'use strict';

  if (typeof window === 'undefined' || !('customElements' in window)) return;
  if (customElements.get('dd-bundle-guide')) return;

  var QTY_SELECTORS = [
    'product-info quantity-input input[type="number"]',
    'product-info input[name="quantity"]',
    'main quantity-input input[type="number"]',
    'main input[name="quantity"]',
  ];

  var ATC_SELECTORS = [
    'product-info .product-form__submit',
    'product-info button[name="add"]',
    '.product-form__submit',
    'button[name="add"]',
  ];

  function findQtyInput() {
    for (var i = 0; i < QTY_SELECTORS.length; i++) {
      var el = document.querySelector(QTY_SELECTORS[i]);
      if (el) return el;
    }
    return null;
  }

  function findAtcTarget() {
    for (var i = 0; i < ATC_SELECTORS.length; i++) {
      var el = document.querySelector(ATC_SELECTORS[i]);
      if (el && isVisibleInLayout(el)) return el;
    }
    // Sticky footer is the always-visible fallback on mobile (the
    // main ATC may be far up the page when the bundle guide is in
    // view). syncButtonFromRenderedHtml inside the sticky has its own
    // visibility logic; we just need a scroll anchor.
    var sticky = document.querySelector('dd-sticky-footer');
    if (sticky) return sticky;
    return null;
  }

  function isVisibleInLayout(el) {
    // Cheap visibility check — element exists in flow and has a
    // measurable box. We don't care if it's currently in viewport,
    // only that scrolling to it will land somewhere meaningful.
    if (!el) return false;
    var rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  class DDBundleGuide extends HTMLElement {
    constructor() {
      super();
      this._cards = [];
      this._track = null;
      this._drag = null;
      this._didDrag = false;
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onDragClickCapture = this._onDragClickCapture.bind(this);
    }

    connectedCallback() {
      this._cards = Array.prototype.slice.call(
        this.querySelectorAll('[data-bundle-card]')
      );
      this._track = this.querySelector('[id^="Slider-"]');

      if (this.dataset.interactive !== 'false') {
        this._bindCardClicks();
      }
      this._bindDrag();
    }

    disconnectedCallback() {
      if (!this._track) return;
      this._track.removeEventListener('pointerdown', this._onPointerDown);
      this._track.removeEventListener('pointermove', this._onPointerMove);
      this._track.removeEventListener('pointerup', this._onPointerUp);
      this._track.removeEventListener('pointercancel', this._onPointerUp);
      this._track.removeEventListener('click', this._onDragClickCapture, true);
    }

    _isCarousel() {
      return window.matchMedia('(max-width: 1023px)').matches;
    }

    _bindDrag() {
      if (!this._track) return;
      this._track.addEventListener('pointerdown', this._onPointerDown);
      this._track.addEventListener('click', this._onDragClickCapture, true);
    }

    _onPointerDown(e) {
      if (!this._isCarousel()) return;
      if (e.pointerType === 'touch') return;
      if (e.button !== 0) return;
      this._drag = {
        id: e.pointerId,
        x: e.clientX,
        scroll: this._track.scrollLeft,
        moved: false,
      };
      this._track.addEventListener('pointermove', this._onPointerMove);
      this._track.addEventListener('pointerup', this._onPointerUp);
      this._track.addEventListener('pointercancel', this._onPointerUp);
    }

    _onPointerMove(e) {
      if (!this._drag || e.pointerId !== this._drag.id) return;
      var dx = e.clientX - this._drag.x;
      if (!this._drag.moved && Math.abs(dx) < 8) return;
      this._drag.moved = true;
      this._didDrag = true;
      try {
        this._track.setPointerCapture(e.pointerId);
      } catch (err) {
        /* capture can fail if the pointer already released */
      }
      this._track.scrollLeft = this._drag.scroll - dx;
    }

    _onPointerUp(e) {
      if (!this._drag || e.pointerId !== this._drag.id) return;
      this._track.removeEventListener('pointermove', this._onPointerMove);
      this._track.removeEventListener('pointerup', this._onPointerUp);
      this._track.removeEventListener('pointercancel', this._onPointerUp);
      try {
        if (this._track.hasPointerCapture(e.pointerId)) {
          this._track.releasePointerCapture(e.pointerId);
        }
      } catch (err) {
        console.error('[dd-bundle-guide] pointer capture release', err);
      } finally {
        this._drag = null;
      }
    }

    _onDragClickCapture(e) {
      if (!this._didDrag) return;
      e.preventDefault();
      e.stopPropagation();
      this._didDrag = false;
    }

    _bindCardClicks() {
      var self = this;
      this._cards.forEach(function (card) {
        var qty = parseInt(card.getAttribute('data-qty-value'), 10);
        if (!Number.isFinite(qty) || qty <= 0) return;
        // Native <button> would already fire click on Enter/Space;
        // the inert (qty=0) path renders a div instead — those skip
        // wiring entirely, so no handler needed.
        card.addEventListener('click', function (e) {
          e.preventDefault();
          self._activate(card, qty);
        });
      });
    }

    _activate(card, qty) {
      this._highlight(card);
      var qtyApplied = this._setQty(qty);
      this._scrollToAtc();
      this.dispatchEvent(
        new CustomEvent('dd:bundle-guide:select', {
          bubbles: true,
          detail: { qty: qty, qtyApplied: qtyApplied },
        })
      );
    }

    _highlight(card) {
      this._cards.forEach(function (c) {
        if (c === card) return;
        if (c.hasAttribute('aria-pressed')) {
          c.setAttribute('aria-pressed', 'false');
        }
        c.classList.remove('is--selected');
      });
      if (card.hasAttribute('aria-pressed')) {
        card.setAttribute('aria-pressed', 'true');
      }
      card.classList.add('is--selected');
    }

    _setQty(qty) {
      var input = findQtyInput();
      if (!input) return false;
      var max = parseInt(input.getAttribute('max'), 10);
      var clamped = Number.isFinite(max) && max > 0 ? Math.min(qty, max) : qty;
      input.value = String(clamped);
      // Dispatch both events: 'input' is what Dawn's <quantity-input>
      // listens for to update its +/- button enabled state, 'change'
      // is what most cart-aware listeners use.
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    _scrollToAtc() {
      var target = findAtcTarget();
      if (!target) return;
      try {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) {
        // Older browsers without smooth-scroll options support fall
        // back to a no-arg scrollIntoView (instant jump). Acceptable.
        target.scrollIntoView();
      }
    }

  }

  customElements.define('dd-bundle-guide', DDBundleGuide);
})();
